import { useEffect, useState, useRef } from "react";
import { Radio } from "lucide-react";

import { api, API } from "../lib/api";

function relTime(iso) {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s temu`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min temu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h temu`;
  const d = Math.floor(h / 24);
  return `${d} d temu`;
}

/**
 * Floating notification toaster — bottom-right.
 * Pops up the latest vote as a chip every time a new vote comes in (or every interval).
 *
 * variant:
 *   - "editorial" (default): square corners, magazine-style label
 *   - "apple": rounded-full pill, soft shadow, fits the Apple-style landing
 */
export default function FloatingVoteNotifier({ variant = "editorial" }) {
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);
  const seenIdsRef = useRef(new Set());
  const idxRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let hideTimer;
    let pollTimer;

    const show = (vote) => {
      if (cancelled) return;
      setCurrent(vote);
      setVisible(true);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setVisible(false), 5500);
    };

    const fetchLatest = async (preferUnseen = true) => {
      try {
        const { data } = await api.get("/votes/recent?limit=6");
        if (cancelled || !data?.length) return;
        if (preferUnseen) {
          const unseen = data.find(
            (v) => !seenIdsRef.current.has(`${v.created_at}-${v.city}`)
          );
          if (unseen) {
            show(unseen);
            seenIdsRef.current.add(`${unseen.created_at}-${unseen.city}`);
            return;
          }
        }
        if (idxRef.current === 0) {
          show(data[0]);
          seenIdsRef.current.add(`${data[0].created_at}-${data[0].city}`);
        } else {
          const cyclic = data[idxRef.current % data.length];
          if (cyclic) show(cyclic);
        }
        idxRef.current += 1;
      } catch {
        /* silent */
      }
    };

    // First load: show latest
    fetchLatest();

    // Realtime via SSE: instant push on every vote
    let es;
    try {
      es = new EventSource(`${API}/events`);
      es.addEventListener("vote", (e) => {
        try {
          const payload = JSON.parse(e.data);
          const voteShape = {
            city: payload.city,
            skatepark_name: payload.skatepark,
            created_at: payload.ts,
          };
          const key = `${voteShape.created_at}-${voteShape.city}`;
          if (seenIdsRef.current.has(key)) return;
          seenIdsRef.current.add(key);
          show(voteShape);
        } catch {
          /* ignore malformed event */
        }
      });
      es.onerror = () => {
        // The browser will auto-reconnect; nothing to do here
      };
    } catch {
      /* EventSource not supported — pure poll fallback */
    }

    // Visibility-aware fallback poll: only when SSE is silent or browser is back from hidden
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      fetchLatest(false);
    };
    pollTimer = setInterval(tick, 30000);
    const onVis = () => {
      if (document.visibilityState === "visible") fetchLatest();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      clearInterval(pollTimer);
      clearTimeout(hideTimer);
      document.removeEventListener("visibilitychange", onVis);
      if (es) es.close();
    };
  }, []);

  if (!current) return null;

  if (variant === "apple") {
    return (
      <div
        data-testid="floating-vote-notifier"
        className={`fixed bottom-5 right-5 z-50 max-w-xs transition-all duration-500 ease-out ${
          visible
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-3 pointer-events-none"
        }`}
      >
        <div
          className="surface-card flex items-center gap-2.5 pl-3 pr-4 py-2.5"
          style={{ borderRadius: 9999 }}
        >
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-brand-green opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-green" />
          </span>
          <p className="text-[12px] text-black/80 truncate">
            <span className="text-[9px] font-medium uppercase tracking-[0.25em] brand-green mr-2">
              Live
            </span>
            Ktoś zagłosował na{" "}
            <strong className="font-semibold text-black">{current.city}</strong>
            <span className="text-black/40 text-[11px] ml-1.5 tabular-nums">
              · {relTime(current.created_at)}
            </span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="floating-vote-notifier"
      className={`fixed bottom-5 right-5 z-50 max-w-xs transition-all duration-500 ease-out ${
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-3 pointer-events-none"
      }`}
    >
      <div className="bg-white border border-black/10 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.25)] px-4 py-3 flex items-center gap-3">
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full rounded-full bg-brand-green opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-green" />
        </span>
        <div className="min-w-0">
          <p className="text-[9px] font-medium uppercase tracking-[0.25em] text-black/40 flex items-center gap-1.5">
            <Radio className="w-2.5 h-2.5 brand-green" /> Live · nowy głos
          </p>
          <p className="text-sm text-black mt-0.5 truncate">
            Ktoś zagłosował na{" "}
            <strong className="font-semibold">{current.city}</strong>
            <span className="text-black/40 text-xs ml-1.5 tabular-nums">
              · {relTime(current.created_at)}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
